/**
 * ============================================================================
 * TRANSACTION HISTORY COMPONENT - 100% Ready
 * ============================================================================
 */

import React, { useState } from 'react';
import { useWalletStore } from '../../store';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import EmptyState from '../common/EmptyState';
import StatusBadge from '../common/StatusBadge';
import Tabs from '../common/Tabs';

/**
 * Transaction History Component
 * @returns {JSX.Element}
 */
const TransactionHistory = () => {
  const { transactions: rawTransactions } = useWalletStore();
  const transactions = Array.isArray(rawTransactions) ? rawTransactions : [];
  const { formatCurrency, formatDate, formatTime, language } = useI18n();
  const [activeFilter, setActiveFilter] = useState('all');

  if (transactions.length === 0) {
    return (
      <Card variant="glass" className="p-12">
        <EmptyState
          icon="📊"
          title={language === 'fr' ? 'Aucune transaction' : 'No transactions yet'}
          description={
            language === 'fr'
              ? 'Vos transactions de portefeuille apparaîtront ici'
              : 'Your wallet transactions will appear here'
          }
        />
      </Card>
    );
  }

  const getTransactionIcon = (type) => {
    const icons = {
      credit: '💰',
      debit: '💸',
      escrow_lock: '🔒',
      escrow_release: '🔓',
      withdrawal: '⬇️',
      refund: '↩️',
      fee: '💳',
      bonus: '🎁',
      reversal: '↪️',
    };
    return icons[type] || '📊';
  };

  const getTransactionColor = (type) => {
    const colors = {
      credit: 'from-green-500 to-emerald-500',
      debit: 'from-red-500 to-pink-500',
      escrow_lock: 'from-blue-500 to-cyan-500',
      escrow_release: 'from-green-500 to-teal-500',
      withdrawal: 'from-purple-500 to-pink-500',
      refund: 'from-yellow-500 to-orange-500',
      fee: 'from-gray-500 to-slate-500',
      bonus: 'from-purple-500 to-pink-500',
      reversal: 'from-red-500 to-orange-500',
    };
    return colors[type] || 'from-gray-500 to-slate-500';
  };

  const getTransactionLabel = (type) => {
    const labels = {
      credit: language === 'fr' ? 'Crédit' : 'Credit',
      debit: language === 'fr' ? 'Débit' : 'Debit',
      escrow_lock: language === 'fr' ? 'Verrouillage escrow' : 'Escrow Lock',
      escrow_release: language === 'fr' ? 'Libération escrow' : 'Escrow Release',
      withdrawal: language === 'fr' ? 'Retrait' : 'Withdrawal',
      refund: language === 'fr' ? 'Remboursement' : 'Refund',
      fee: language === 'fr' ? 'Frais' : 'Fee',
      bonus: language === 'fr' ? 'Bonus' : 'Bonus',
      reversal: language === 'fr' ? 'Annulation' : 'Reversal',
    };
    return labels[type] || type;
  };

  const filteredTransactions =
    activeFilter === 'all'
      ? transactions
      : transactions.filter((t) => t.type === activeFilter);

  const tabs = [
    { id: 'all', label: language === 'fr' ? 'Tout' : 'All', count: transactions.length },
    {
      id: 'credit',
      label: language === 'fr' ? 'Crédits' : 'Credits',
      count: transactions.filter((t) => t.type === 'credit').length,
    },
    {
      id: 'debit',
      label: language === 'fr' ? 'Débits' : 'Debits',
      count: transactions.filter((t) => t.type === 'debit').length,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeFilter} onTabChange={setActiveFilter} variant="pills" />

      {/* Transactions List */}
      <div className="space-y-3">
        {filteredTransactions.map((transaction, index) => (
          <Card
            key={transaction.id}
            variant="glass"
            hover
            className="animate-fadeInUp"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getTransactionColor(
                  transaction.type
                )} flex items-center justify-center text-xl shadow-lg flex-shrink-0`}
              >
                {getTransactionIcon(transaction.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-bold text-gray-900 dark:text-white">
                    {transaction.description || getTransactionLabel(transaction.type)}
                  </p>
                  <StatusBadge
                    variant={
                      transaction.status === 'completed'
                        ? 'success'
                        : transaction.status === 'pending'
                        ? 'warning'
                        : 'error'
                    }
                    size="sm"
                  >
                    {transaction.status}
                  </StatusBadge>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  {transaction.createdAt && (
                    <>
                      <span>{formatDate(transaction.createdAt)}</span>
                      <span>•</span>
                      <span>{formatTime(transaction.createdAt)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div
                  className={`text-lg font-black ${
                    transaction.type === 'credit'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {transaction.type === 'credit' ? '+' : '-'}
                  {formatCurrency(
                    Math.abs(transaction.amount || 0),
                    transaction.currency || 'XAF'
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TransactionHistory;
